/**
 * AccordionGroup - Manages a group of accordions with mutual exclusion behavior
 * and synchronized media state changes.
 *
 * @example
 * <accordion-group data-active-class="is-active">
 *   <details class="accordion" data-accordion-id="1">...</details>
 *   <details class="accordion" data-accordion-id="2">...</details>
 * </accordion-group>
 */
if (!customElements.get('accordion-group')) {
  class AccordionGroup extends HTMLElement {
    #accordions = [];
    #currentId = null;
    #animationDuration = 400;

    connectedCallback() {
      this.#accordions = [...this.querySelectorAll('.accordion')];
      this.#setupEventDelegation();
      this.#initializeState();
    }

    #setupEventDelegation() {
      this.addEventListener('click', (event) => {
        const summary = event.target?.closest('.accordion__header');
        if (!summary) return;

        // Let links inside the header (e.g. "Shop") navigate instead of toggling
        if (event.target.closest('a[href]')) return;

        event.preventDefault();

        const accordion = summary.closest('.accordion');
        if (!accordion) return;

        const accordionId = accordion.dataset.accordionId;

        if (accordion.open) {
          this.#closeAccordion(accordion);
        } else {
          this.#openAccordion(accordion, accordionId);
        }
      });
    }

    #initializeState() {
      const openAccordion = this.#accordions.find((acc) => acc.open);

      if (openAccordion) {
        this.#currentId = openAccordion.dataset.accordionId;
        openAccordion.classList.add('is-active');
        this.#dispatchChange(this.#currentId);
      }
    }

    #openAccordion(accordion, accordionId) {
      this.#closeAllAccordions();

      accordion.open = true;
      accordion.classList.add('is-active');

      const content = accordion.querySelector('.accordion__content');
      if (content) {
        content.style.gridTemplateRows = '0fr';
        requestAnimationFrame(() => {
          content.style.gridTemplateRows = '1fr';
        });
      }

      this.#currentId = accordionId;
      this.#dispatchChange(accordionId);
    }

    #closeAccordion(accordion) {
      const content = accordion.querySelector('.accordion__content');
      if (!content) return;

      accordion.classList.remove('is-active');

      const handleTransitionEnd = (e) => {
        if (e.target !== content) return;
        if (e.propertyName !== 'grid-template-rows') return;

        accordion.open = false;
        content.style.gridTemplateRows = '';
        content.removeEventListener('transitionend', handleTransitionEnd);
      };

      content.addEventListener('transitionend', handleTransitionEnd);

      requestAnimationFrame(() => {
        content.style.gridTemplateRows = '0fr';
      });

      setTimeout(() => {
        content.removeEventListener('transitionend', handleTransitionEnd);
        if (accordion.open) {
          accordion.open = false;
          content.style.gridTemplateRows = '';
        }
      }, this.#animationDuration + 100);
    }

    #closeAllAccordions() {
      for (const accordion of this.#accordions) {
        if (!accordion.open) continue;

        const content = accordion.querySelector('.accordion__content');
        accordion.open = false;
        accordion.classList.remove('is-active');

        if (content) {
          content.style.gridTemplateRows = '';
        }
      }
    }

    #dispatchChange(accordionId) {
      this.dispatchEvent(
        new CustomEvent('accordion:change', {
          bubbles: true,
          detail: { accordionId },
        })
      );
    }
  }

  customElements.define('accordion-group', AccordionGroup);
}
